<template>
  <div>
    <!-- Toolbar -->
    <v-row align="center" class="mb-4" dense>
      <v-col>
        <v-select
          v-model="appezzamento_selezionato"
          :items="appezzamenti"
          item-text="nome"
          return-object
          label="Seleziona appezzamento"
          outlined
          dense
          hide-details
          clearable
          :disabled="!appezzamenti.length"
        >
          <template #item="{ item }">
            <div class="py-1">
              <div class="text-body-2 font-weight-medium">{{ item.nome }}</div>
              <div class="caption grey--text">{{ item.larghezza || '?' }}×{{ item.lunghezza || '?' }} m</div>
            </div>
          </template>
        </v-select>
      </v-col>
      <v-col cols="auto" class="d-flex" style="gap:8px">
        <v-btn outlined small color="blue-grey darken-1" @click="$refs.gestione_appezzamento.openDialog()">
          <v-icon small class="mr-1">mdi-map</v-icon>
          Appezzamenti
        </v-btn>
        <v-btn outlined small color="green darken-1" @click="$refs.gestione_serre.openDialog()">
          <v-icon small class="mr-1">mdi-greenhouse</v-icon>
          Serre
        </v-btn>
      </v-col>
    </v-row>

    <!-- Canvas -->
    <v-progress-linear v-if="loading_posizioni" indeterminate class="mb-2" />
    <template v-else-if="appezzamento_selezionato">
      <AppezzamentoCanvas
        :appezzamento="appezzamento_selezionato"
        :posizioni="posizioni_appezzamento"
        :serre="serre"
        :blocchi="blocchi_appezzamento"
        :readonly="false"
        :completamento_serre="{}"
        :recap_per_serra="{}"
        @serra-selezionata="onSerraSelezionata"
        @modifica-serra="onModificaSerra"
        @posizione-aggiunta="onPosizioneAggiunta"
        @posizione-aggiornata="onPosizioneAggiornata"
        @posizione-rimossa="onPosizioneRimossa"
        @blocco-aggiunto="onBloccoAggiunto"
        @blocco-aggiornato="onBloccoAggiornato"
        @blocco-rimosso="onBloccoRimosso"
      />
    </template>
    <v-alert v-else-if="appezzamenti.length" outlined class="mt-2">
      Seleziona un appezzamento dal menu sopra per visualizzare la mappa.
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-2">
      Nessun appezzamento configurato. Clicca su "Appezzamenti" per crearne uno.
    </v-alert>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" top timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>

    <!-- Dialogs -->
    <GestioneSerreDialog
      ref="gestione_serre"
      @serre-aggiornate="onSerreAggiornate"
    />
    <GestioneAppezzamentoDialog
      ref="gestione_appezzamento"
      @appezzamenti-aggiornati="onAppezzamentiAggiornati"
    />
  </div>
</template>

<script>
import AppezzamentoCanvas from "@/components/PianoColtivazione/Desktop/AppezzamentoCanvas.vue";
import GestioneSerreDialog from "@/components/PianoColtivazione/Desktop/GestioneSerreDialog.vue";
import GestioneAppezzamentoDialog from "@/components/PianoColtivazione/Desktop/GestioneAppezzamentoDialog.vue";

export default {
  name: "MappAziendaleDesktop",
  components: { AppezzamentoCanvas, GestioneSerreDialog, GestioneAppezzamentoDialog },
  data() {
    return {
      appezzamento_selezionato: null,
      loading_posizioni: false,
      snackbar: { visible: false, color: "success", message: "" },
    };
  },
  mounted() {
    if (!this.appezzamento_selezionato && this.appezzamenti.length) {
      this.appezzamento_selezionato = this.appezzamenti[0];
    }
  },
  computed: {
    appezzamenti() {
      return this.$store.getters.getAppezzamenti || [];
    },
    serre() {
      return this.$store.getters.getSerre || [];
    },
    posizioni_appezzamento() {
      if (!this.appezzamento_selezionato) return [];
      return this.$store.getters.getPosizioniAppezzamento(this.appezzamento_selezionato.id);
    },
    blocchi_appezzamento() {
      if (!this.appezzamento_selezionato) return [];
      return this.$store.getters.getBlocchiAppezzamento(this.appezzamento_selezionato.id);
    },
  },
  watch: {
    appezzamento_selezionato(app) {
      if (!app) return;
      this.loading_posizioni = true;
      Promise.all([
        this.$store.dispatch("fetchPosizioniAppezzamento", app.id),
        this.$store.dispatch("fetchBlocchiAppezzamento", app.id),
      ]).finally(() => { this.loading_posizioni = false; });
    },
  },
  methods: {
    onSerraSelezionata(id_serra) {
      this.$refs.gestione_serre.openDialogConSerra(id_serra);
    },
    onModificaSerra(id_serra) {
      this.$refs.gestione_serre.openDialogConSerra(id_serra);
    },
    onPosizioneAggiunta({ id_serra, cx, cy }) {
      if (!this.appezzamento_selezionato) return;
      this.$store.dispatch("savePosizioneAppezzamento", {
        id_appezzamento: this.appezzamento_selezionato.id,
        payload: { id_serra, x: cx, y: cy },
      }).catch(() => this.mostraSnackbar("Errore durante il posizionamento.", "error"));
    },
    onPosizioneAggiornata({ id, cx, cy, rotazione }) {
      this.$store.dispatch("updatePosizioneAppezzamento", {
        id_posizione: id,
        payload: { x: cx, y: cy, rotazione },
      }).catch(() => this.mostraSnackbar("Errore durante il salvataggio della posizione.", "error"));
    },
    onPosizioneRimossa(id_posizione) {
      if (!this.appezzamento_selezionato) return;
      this.$store.dispatch("deletePosizioneAppezzamento", {
        id_posizione,
        id_appezzamento: this.appezzamento_selezionato.id,
      }).catch(() => this.mostraSnackbar("Errore durante la rimozione.", "error"));
    },
    onBloccoAggiunto({ cx, cy, larghezza, lunghezza, rotazione, label, colore }) {
      if (!this.appezzamento_selezionato) return;
      this.$store.dispatch("saveBloccoAppezzamento", {
        id_appezzamento: this.appezzamento_selezionato.id,
        payload: { x: cx, y: cy, larghezza, lunghezza, rotazione, label, colore },
      }).catch(() => this.mostraSnackbar("Errore durante l'aggiunta del blocco.", "error"));
    },
    onBloccoAggiornato({ id, cx, cy, rotazione, larghezza, lunghezza, label, colore }) {
      this.$store.dispatch("updateBloccoAppezzamento", {
        id_blocco: id,
        payload: { x: cx, y: cy, rotazione, larghezza, lunghezza, label, colore },
      }).catch(() => this.mostraSnackbar("Errore durante il salvataggio del blocco.", "error"));
    },
    onBloccoRimosso(id_blocco) {
      if (!this.appezzamento_selezionato) return;
      this.$store.dispatch("deleteBloccoAppezzamento", {
        id_blocco,
        id_appezzamento: this.appezzamento_selezionato.id,
      }).catch(() => this.mostraSnackbar("Errore durante la rimozione del blocco.", "error"));
    },
    onSerreAggiornate() {},
    onAppezzamentiAggiornati() {
      if (this.appezzamento_selezionato) {
        const aggiornato = this.appezzamenti.find(
          (a) => a.id === this.appezzamento_selezionato.id
        );
        this.appezzamento_selezionato = aggiornato || null;
      }
    },
    mostraSnackbar(message, color) {
      this.snackbar = { visible: true, message, color };
    },
  },
};
</script>
